import { useState } from 'react';
import * as medicalRecordService from '../services/medicalRecordService';
import { useUIStore } from '../stores/uiStore';
import { IMedicalRecordFiltersDTO } from '../types/interfaces/IMedicalRecordFilters';
import { notifyApiError } from '../services/core/apiErrorUX';
import { notifyApiSuccess } from '../services/core/apiSuccessUX';

// Hook específico para historias clínicas
export function useMedicalRecords() {
  const [loading, setLoading] = useState(false);
  const { addNotification } = useUIStore();

  const getAllRecords = async (filters: IMedicalRecordFiltersDTO) => {
    setLoading(true);
    try {
      const response = await medicalRecordService.getMedicalRecords(filters);

      if (response.success && response.data) {
        return response;
      } else {
        notifyApiError(addNotification, response, {
          context: 'module',
          fallbackMessage: 'Error al cargar historias clínicas',
        });
        return response;
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Error al cargar historias clínicas',
      });
      return {
        success: false,
        data: null,
        error: 'Error al cargar historias clínicas',
      };
    } finally {
      setLoading(false);
    }
  };

  const getRecordById = async (id: string) => {
    setLoading(true);
    try {
      const response = await medicalRecordService.getMedicalRecordById(id);

      if (response.success && response.data) {
        return response;
      } else {
        notifyApiError(addNotification, response, {
          context: 'module',
          fallbackMessage: 'Error al cargar historia clínica',
        });
        return response;
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Error al cargar historia clínica',
      });
      return {
        success: false,
        data: null,
        error: 'Error al cargar historia clínica',
      };
    } finally {
      setLoading(false);
    }
  };

  const createRecord = async (recordData: any) => {
    setLoading(true);
    try {
      const response = await medicalRecordService.createMedicalRecord(recordData);

      if (response.success && response.data) {
        notifyApiSuccess(addNotification, 'Historia Clínica Creada', 'Historia clínica creada correctamente');
        return response;
      } else {
        notifyApiError(addNotification, response, {
          context: 'module',
          fallbackMessage: 'Error al crear historia clínica',
        });
        return response;
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Error al crear historia clínica',
      });
      return {
        success: false,
        data: null,
        error: 'Error al crear historia clínica',
      };
    } finally {
      setLoading(false);
    }
  };

  const updateRecord = async (id: string, recordData: any) => {
    setLoading(true);
    try {
      const response = await medicalRecordService.updateMedicalRecord(id, recordData);

      if (response.success && response.data) {
        notifyApiSuccess(addNotification, 'Historia Clínica Actualizada', 'Historia clínica actualizada correctamente');
        return response;
      } else {
        notifyApiError(addNotification, response, {
          context: 'module',
          fallbackMessage: 'Error al actualizar historia clínica',
        });
        return response;
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Error al actualizar historia clínica',
      });
      return {
        success: false,
        data: null,
        error: 'Error al actualizar historia clínica',
      };
    } finally {
      setLoading(false);
    }
  };

  const deleteRecord = async (id: string) => {
    setLoading(true);
    try {
      const response = await medicalRecordService.deleteMedicalRecord(id);

      if (response.success) {
        notifyApiSuccess(addNotification, 'Historia Clínica Eliminada', 'Historia clínica eliminada correctamente');
        return response;
      } else {
        notifyApiError(addNotification, response, {
          context: 'module',
          fallbackMessage: 'Error al eliminar historia clínica',
        });
        return response;
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Error al eliminar historia clínica',
      });
      return {
        success: false,
        data: null,
        error: 'Error al eliminar historia clínica',
      };
    } finally {
      setLoading(false);
    }
  };

  return {
    getAllRecords,
    getRecordById,
    createRecord,
    updateRecord,
    deleteRecord,
    loading,
  };
}
